<template>
  <div class="relative w-full">
    <!-- Bouton déclencheur principal -->
    <button
      @click="toggleDropdown"
      class="w-full flex items-center gap-3 p-2 rounded-xl border text-xs transition-all duration-200 focus:outline-none"
      :class="isDark 
        ? 'bg-zinc-900/40 border-zinc-900 hover:bg-zinc-900 text-zinc-400 hover:text-zinc-200' 
        : 'bg-zinc-50 border-zinc-200/60 hover:bg-zinc-100/80 text-zinc-600 hover:text-zinc-900'"
      title="Changer de thème"
    >
      <div class="flex items-center justify-center shrink-0 w-4 h-4">
        <!-- Icône dynamique selon le mode actif réel -->
        <svg v-if="currentTheme === 'light'" class="w-4 h-4 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v2.25m0 13.5V21M4.22 4.22l1.59 1.59m12.38 12.38l1.59 1.59M3 12h2.25m13.5 0H21M4.22 19.78l1.59-1.59M18.14 5.86l1.59-1.59M12 7.5a4.5 4.5 0 100 9 4.5 4.5 0 000-9z" />
        </svg>
        <svg v-else-if="currentTheme === 'dark'" class="w-4 h-4 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
        </svg>
        <svg v-else class="w-4 h-4 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z" />
        </svg>
      </div>
      
      <!-- Texte masqué automatiquement via le parent si la sidebar est repliée -->
      <span class="tracking-wide font-normal truncate class-label">
        Thème : {{ currentThemeLabel }}
      </span>
    </button>

    <!-- Menu déroulant (Ouverture vers le haut car placé en bas de barre latérale) -->
    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95 translate-y-2"
      enter-to-class="transform opacity-100 scale-100 translate-y-0"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100 translate-y-0"
      leave-to-class="transform opacity-0 scale-95 translate-y-2"
    >
      <div
        v-if="isOpen"
        class="absolute bottom-full left-0 mb-2 w-48 rounded-xl shadow-xl border overflow-hidden z-50 p-1"
        :class="isDark 
          ? 'bg-zinc-900 border-zinc-800 shadow-black/60' 
          : 'bg-white border-zinc-200/70 shadow-zinc-200/50'"
      >
        <div class="space-y-0.5">
          <button
            v-for="option in themeOptions"
            :key="option.value"
            @click="selectTheme(option.value)"
            class="w-full text-left px-3 py-2 text-xs rounded-lg transition-colors duration-150 flex items-center justify-between group"
            :class="[
              currentTheme === option.value
                ? (isDark ? 'bg-zinc-800 text-white font-medium' : 'bg-zinc-100 text-zinc-900 font-semibold')
                : (isDark ? 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200' : 'text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900')
            ]"
          >
            <span class="flex items-center gap-2.5">
              <!-- Rendu direct des icônes SVG sans template imbriqué -->
              <svg v-if="option.value === 'light'" class="w-3.5 h-3.5 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v2.25m0 13.5V21M4.22 4.22l1.59 1.59m12.38 12.38l1.59 1.59M3 12h2.25m13.5 0H21M4.22 19.78l1.59-1.59M18.14 5.86l1.59-1.59M12 7.5a4.5 4.5 0 100 9 4.5 4.5 0 000-9z" />
              </svg>
              <svg v-else-if="option.value === 'dark'" class="w-3.5 h-3.5 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
              </svg>
              <svg v-else class="w-3.5 h-3.5 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z" />
              </svg>
              {{ option.label }}
            </span>
            
            <!-- Checkmark d'activation discrète -->
            <svg
              v-if="currentTheme === option.value"
              class="h-3.5 w-3.5"
              :class="isDark ? 'text-zinc-400' : 'text-zinc-900'"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useTheme } from '@/composables/useTheme'

const { currentTheme, isDark, setTheme } = useTheme()
const isOpen = ref(false)

const themeOptions = [
  { value: 'light', label: 'Clair' },
  { value: 'dark', label: 'Sombre' },
  { value: 'auto', label: 'Automatique' }
]

// Label dynamique basé sur la valeur du thème
const currentThemeLabel = computed(() => {
  const match = themeOptions.find(o => o.value === currentTheme.value)
  return match ? match.label : 'Automatique'
})

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const selectTheme = (theme) => {
  setTheme(theme)
  isOpen.value = false
}

// Gestion saine de la fermeture sur clic extérieur
const handleClickOutside = (event) => {
  const container = event.target.closest('.relative')
  if (!container && isOpen.value) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Pour masquer proprement le label si la classe parente applique une réduction à la sidebar */
.w-20 .class-label {
  display: none;
}
</style>